<template>
    <div :class="{ 'was-validated': $v.variantId.$error }">
        <!-- Standard Radio Button Display (Default) -->
        <div v-if="!isRangeQuestion && !isYesNoQuestion">
            <div
                :key="variant.id"
                class="radio-option"
                v-for="variant of answer.variants"
            >
                <input
                    :id="`SingleItemsAnswer${questionId}-${variant.id}`"
                    :name="`answerForQuestion${questionId}`"
                    :value="variant.id"
                    :disabled="isReadOnly"
                    class="radio-input"
                    type="radio"
                    required
                    @click="() => radioButtonClicked(variant.id, variant.text)"
                    :checked="selectedAnswer && selectedAnswer.answer === variant.id"
                />
                <label
                    class="radio-label"
                    :for="`SingleItemsAnswer${questionId}-${variant.id}`"
                >
                    {{ variant.text }}
                </label>
            </div>
        </div>

        <!-- Range Question Display -->
        <div v-if="isRangeQuestion" class="range-question-container">
            <div 
                v-for="variant of filteredRangeVariants" 
                :key="variant.id"
                class="range-item"
            >
                <div class="range-item-text">{{ variant.text }}</div>
                <div class="range-options">
                    <label
                        v-for="(value, index) in variant.scoringValues"
                        :key="`${variant.id}-${index}`"
                        class="range-option"
                        :class="{ 'selected': rangeSelections[variant.id] == value }"
                    >
                        <input
                            type="radio"
                            :name="`range-${questionId}-${variant.id}`"
                            :value="value"
                            :disabled="isReadOnly"
                            @change="() => onRangeSelected(variant.id, value, variant.text)"
                            :checked="rangeSelections[variant.id] == value"
                            class="range-radio"
                        />
                        <span class="range-value">{{ value }}</span>
                    </label>
                </div>
            </div>
            <div class="total-score-container">
                <span class="total-score-label">Total Score:</span>
                <span class="total-score-value">{{ totalScore }}</span>
            </div>
        </div>

        <!-- Yes/No Question Display -->
        <div v-if="isYesNoQuestion" class="yesno-question-container">
            <div 
                v-for="variant of filteredYesNoVariants" 
                :key="variant.id"
                class="yesno-item"
            >
                <div class="yesno-item-text">{{ variant.text }}</div>
                <div class="yesno-switch-container">
                    <label class="switch">
                        <input
                            type="checkbox"
                            :disabled="isReadOnly"
                            :checked="yesNoSelections[variant.id]"
                            @change="onYesNoToggle(variant.id, $event, variant.text)"
                        />
                        <span class="slider"></span>
                        <span class="switch-labels">
                            <span class="yes-label">Yes</span>
                            <span class="no-label">No</span>
                        </span>
                    </label>
                </div>
            </div>
            <div class="total-score-container">
                <span class="total-score-label">Total Score:</span>
                <span class="total-score-value">{{ totalScore }}</span>
            </div>
        </div>

        <div class="" v-if="isUser">
            <button 
                class="btn-saved"
                @click="onVariantIdChanged" 
                :disabled="!canSave" 
            >
               
                Save
            </button>

            <report-button 
                questionText="Do you want to report an error?"
                callToActionText="Write us!"
                @click.native="showModal"
            />
        </div>

        <feedback-modal 
            :modalVisible.sync="modalVisible" 
            :answers.sync="answers" 
            :treatmentHistory.sync="treatmentHistory"
            :excludedRecommendations.sync="excludedRecommendations"
            :branchId.sync="branchId"
        />

        <saving-warning-modal 
            type="single"
            :warningModalVisible.sync="warningModalVisible" 
            :dependentQuestionNames.sync="dependentQuestionNames"
            :questionId="questionId"
            :variantId="variantId"
            :variantText="variantText"
            :localGroups="localGroups"
            :localQuestions="localQuestions"
            :isRangeOrYesNo="isRangeQuestion || isYesNoQuestion"
            :rangeSelections="rangeSelections"
            :yesNoSelections="yesNoSelections"
            :totalScore="totalScore"
            :answers="answers"
            :property="property"
        />
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import constants from "@/entities/constants";
import { SingleItemAnswer } from "@/entities/questionaire/answers/SingleItemAnswer";
import ReportButton from '../../UI/ReportButton.vue';
import FeedbackModal from '../../UI/FeedbackModal.vue';
import { Answer } from "@/entities/questionaire/answers/Answer";
import { Treatment } from "@/entities/Treatment";
import SavingWarningModal from "@/components/UI/SavingWarningModal.vue";
import { Property } from "@/entities/questionaire/Property";
import { Group } from "@/entities/questionaire/Group";

@Component({
    name: constants.answerTypes.SingleItemAnswer.componentName,
    components: {
        ReportButton,
        FeedbackModal,
        SavingWarningModal,
    },
})
export default class AppSingleItemAnswer extends Vue {
    public variantId: number | null = null;
    public variantText: string  = '';
    modalVisible: boolean = false;
    warningModalVisible: boolean = false;
    dependentQuestionNames: string[] = [];
    
    // For range questions
    rangeSelections: { [key: number]: number } = {};
    
    // For yes/no questions
    yesNoSelections: { [key: number]: boolean } = {};
    
    totalScore: number = 0;

    @Prop()
    questionId!: number;

    @Prop()
    answer!: SingleItemAnswer;

    @Prop()
    selectedAnswer!: SingleItemAnswer;

    @Prop()
    isReadOnly!: boolean;

    @Prop()
    isUser!: boolean;

    @Prop()
    answers!: Answer[];

    @Prop()
    treatmentHistory!: Treatment[] | null;

    @Prop()
    branchId!: number | null;

    @Prop()
    excludedRecommendations!: {
        id: number;
        name: string;
        approvalPercentage: number;
    }[] | [];

    @Prop()
    property!: Property;

    @Prop()
    public localGroups!: Group[];

    @Prop()
    localQuestions!: Property[];

    get isRangeQuestion(): boolean {
        return this.property.questionSubType === "HowMany - SelectOne Range";
    }

    get isYesNoQuestion(): boolean {
        return this.property.questionSubType === "HowMany - YesNo";
    }

    get canSave(): boolean {
        if (this.isRangeQuestion) {
            // All filtered variants must be answered
            return this.filteredRangeVariants.every(v => 
                this.rangeSelections[v.id] !== undefined
            );
        } else if (this.isYesNoQuestion) {
            // All filtered variants must be answered
            return this.filteredYesNoVariants.every(v => 
                this.yesNoSelections[v.id] !== undefined
            );
        }
        return this.variantId !== null;
    }

    get filteredRangeVariants() {
        // Filter out variants with empty or missing scoringValues
        return this.answer.variants.filter(v => 
            v.scoringValues && v.scoringValues.length > 0
        );
    }

    get filteredYesNoVariants() {
        // Filter out variants with empty or missing scoringValues
        return this.answer.variants.filter(v => 
            v.scoringValues && v.scoringValues.length > 0
        );
    }

    validations() {
        return {
            variantId: {},
        };
    }

    created() {
        this.initializeValues();
        
        // Check for auto-answer
        if (this.property.autoAnswer && this.property.referenceQuestionId) {
            this.handleAutoAnswer();
        }

        // Listen for re-evaluation events
        this.$events.on('reevaluateAutoAnswer', this.onReevaluateAutoAnswer);
    }

    onReevaluateAutoAnswer(questionId: number) {
        if (this.property.id === questionId && this.property.autoAnswer && this.property.referenceQuestionId) {
            console.log('Re-evaluating auto-answer for question:', this.property.question);
            this.handleAutoAnswer();
        }
    }

    initializeValues() {
        if (this.selectedAnswer) {
            if (this.isRangeQuestion || this.isYesNoQuestion) {
                // Restore saved selections from answer
                const savedScore = this.selectedAnswer.answer;
                if (savedScore !== null && savedScore !== undefined && 
                    this.selectedAnswer.texts && this.selectedAnswer.texts[0]) {
                    try {
                        const selections = JSON.parse(this.selectedAnswer.texts[0]);
                        if (this.isRangeQuestion) {
                            this.rangeSelections = {};
                            Object.keys(selections).forEach(key => {
                                const intKey = parseInt(key);
                                this.rangeSelections[intKey] = Number(selections[key]);
                            });
                        } else {
                            this.yesNoSelections = {};
                            Object.keys(selections).forEach(key => {
                                const intKey = parseInt(key);
                                this.yesNoSelections[intKey] = Boolean(selections[key]);
                            });
                        }
                        this.calculateTotalScore();
                    } catch (e) {
                        console.error('Failed to parse saved selections', e);
                        this.clearDefaults();
                    }
                } else {
                    this.clearDefaults();
                }
            } else {
                this.variantId = this.selectedAnswer.answer;
                this.variantText = this.selectedAnswer.texts[0];
            }
        } else {
            this.clearDefaults();
        }
    }

    clearDefaults() {
        // Initialize default values
        if (this.isRangeQuestion) {
            // Set first scoring value (0) as default for all valid variants
            this.rangeSelections = {};
            this.filteredRangeVariants.forEach(v => {
                if (v.scoringValues && v.scoringValues.length > 0) {
                    // Parse to number to ensure numeric value
                    this.rangeSelections[v.id] = Number(v.scoringValues[0]);
                }
            });
        } else if (this.isYesNoQuestion) {
            // Set "No" (false) as default for all valid variants
            this.yesNoSelections = {};
            this.filteredYesNoVariants.forEach(v => {
                this.yesNoSelections[v.id] = false;
            });
        }
        this.calculateTotalScore();
    }

    handleAutoAnswer() {
        // Find the reference question's answer
        const referenceAnswer = this.answers.find(a => a.PropertyId === this.property.referenceQuestionId);
        
        // Check if it's a SingleItemAnswer and has a numeric answer
        if (referenceAnswer) {
            let score: number | null = null;
            
            // Handle different answer types - check if answer property exists
            if ('answer' in referenceAnswer && typeof (referenceAnswer as any).answer === 'number') {
                score = (referenceAnswer as any).answer;
            }
            
            if (score !== null) {
                // Find matching variant based on score pattern
                const matchingVariant = this.answer.variants.find(v => {
                    const text = this.normalizeText(v.text);
                    
                    // Pattern matching for different formats
                    if (text.includes('-')) {
                        // Range pattern like "6-9 Punkte"
                        const match = text.match(/(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
                        if (match) {
                            const min = parseFloat(match[1]);
                            const max = parseFloat(match[2]);
                            return score >= min && score <= max;
                        }
                    } else if (text.startsWith('>=')) {
                        // Greater than or equal pattern
                        const match = text.match(/>=\s*(\d+(?:\.\d+)?)/);
                        if (match) {
                            const value = parseFloat(match[1]);
                            return score >= value;
                        }
                    } else if (text.startsWith('>')) {
                        // Greater than pattern
                        const match = text.match(/>\s*(\d+(?:\.\d+)?)/);
                        if (match) {
                            const value = parseFloat(match[1]);
                            return score > value;
                        }
                    } else if (text.startsWith('<=')) {
                        // Less than or equal pattern
                        const match = text.match(/<=\s*(\d+(?:\.\d+)?)/);
                        if (match) {
                            const value = parseFloat(match[1]);
                            return score <= value;
                        }
                    } else if (text.startsWith('<')) {
                        // Less than pattern
                        const match = text.match(/<\s*(\d+(?:\.\d+)?)/);
                        if (match) {
                            const value = parseFloat(match[1]);
                            return score < value;
                        }
                    } else if (text.match(/^\d+(?:\.\d+)?\s*Punkte/)) {
                        // Exact value pattern
                        const match = text.match(/^(\d+(?:\.\d+)?)/);
                        if (match) {
                            const value = parseFloat(match[1]);
                            return score === value;
                        }
                    }
                    
                    return false;
                });
                
                if (matchingVariant) {
                    this.variantId = matchingVariant.id;
                    this.variantText = matchingVariant.text;
                    
                    // Automatically trigger save
                    this.$nextTick(() => {
                        this.onVariantIdChanged();
                    });
                }
            }
        }
    }

    normalizeText(str: string): string {
        return String(str || "")
            .trim()
            .replace(/,/g, ".")
            .replace(/≥/g, ">=")
            .replace(/≤/g, "<=")
            .replace(/\s+/g, " ");
    }
    onRangeSelected(variantId: number, value: number | string, text: string) {
        // Ensure value is stored as a number
        this.rangeSelections[variantId] = Number(value);
        this.calculateTotalScore();
        
        if (!this.isUser) {
            this.onVariantIdChanged();
        }
    }

    onYesNoToggle(variantId: number, event: Event, text: string) {
        const checked = (event.target as HTMLInputElement).checked;
        this.yesNoSelections[variantId] = checked;
        this.calculateTotalScore();
        
        if (!this.isUser) {
            this.onVariantIdChanged();
        }
    }

    calculateTotalScore() {
        if (this.isRangeQuestion) {
            // Ensure all values are numbers before summing
            this.totalScore = Object.values(this.rangeSelections).reduce((sum, val) => {
                const numVal = Number(val) || 0;
                return sum + numVal;
            }, 0);
        } else if (this.isYesNoQuestion) {
            this.totalScore = this.filteredYesNoVariants.reduce((sum, variant) => {
                const isYes = this.yesNoSelections[variant.id] === true;
                // Parse scoring values as numbers
                const scoreValue = variant.scoringValues && variant.scoringValues.length > 1 ? 
                    (isYes ? Number(variant.scoringValues[1]) : Number(variant.scoringValues[0])) : 0;
                return sum + scoreValue;
            }, 0);
        }
    }

    radioButtonClicked(newVariantId: number, newVariantText: string) {
        this.variantId = newVariantId;
        this.variantText = newVariantText;
        
        if (!this.isUser) {
            this.onVariantIdChanged();
        }
    }

    // Updated method to handle transitive dependencies through auto-answer chains
    getAllDependentQuestionNames(id: number, visitedIds: Set<number> = new Set()): string[] {
        // Prevent infinite recursion
        if (visitedIds.has(id)) {
            return [];
        }
        visitedIds.add(id);
        
        const allDependents: string[] = [];
        
        // Get direct dependent questions (via questionConditionId)
        const directDependents = this.localQuestions.filter(q => q.questionConditionId === id);
        directDependents.forEach(q => {
            if (!allDependents.includes(q.question)) {
                allDependents.push(q.question);
                // Recursively get their dependents
                const subDependents = this.getAllDependentQuestionNames(q.id, visitedIds);
                subDependents.forEach(subQ => {
                    if (!allDependents.includes(subQ)) {
                        allDependents.push(subQ);
                    }
                });
            }
        });
        
        // Get auto-answer questions that reference this question
        const autoAnswerDependents = this.localQuestions.filter(q => 
            q.autoAnswer === true && q.referenceQuestionId === id
        );
        
        autoAnswerDependents.forEach(autoQ => {
            if (!allDependents.includes(autoQ.question)) {
                allDependents.push(autoQ.question);
            }
            
            // IMPORTANT: Get questions that depend on this auto-answer question's VALUE
            // These are questions where questionConditionId points to the auto-answer question
            const valueDependendents = this.localQuestions.filter(q => 
                q.questionConditionId === autoQ.id
            );
            
            valueDependendents.forEach(valDepQ => {
                if (!allDependents.includes(valDepQ.question)) {
                    allDependents.push(valDepQ.question);
                    // Recursively get their dependents too
                    const subDependents = this.getAllDependentQuestionNames(valDepQ.id, visitedIds);
                    subDependents.forEach(subQ => {
                        if (!allDependents.includes(subQ)) {
                            allDependents.push(subQ);
                        }
                    });
                }
            });
        });
        
        return allDependents;
    }

    checkDependentQuestion(question: Property) {
        const subDepQ = this.localQuestions.filter(q => q.questionConditionId === question.id);
        if (subDepQ.length > 0) {
            for (let i = 0; i < subDepQ.length; i++) {
                subDepQ[i].isHidden = true;
                this.checkDependentQuestion(subDepQ[i]);
            }
        }
    }

   // Add this method to help resolve auto-answer immediately
resolveAutoAnswerImmediate(autoAnswerQuestion: Property, score: number): { variantId: number | null, variantText: string | null } {
    if (!autoAnswerQuestion.answer || !autoAnswerQuestion.answer.variants) {
        return { variantId: null, variantText: null };
    }

    // Find matching variant based on score pattern (same logic as in modal)
    const matchingVariant = autoAnswerQuestion.answer.variants.find(v => {
        const text = this.normalizeText(v.text);
        
        if (text.includes('-')) {
            const match = text.match(/(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
            if (match) {
                const min = parseFloat(match[1]);
                const max = parseFloat(match[2]);
                return score >= min && score <= max;
            }
        } else if (text.startsWith('>=')) {
            const match = text.match(/>=\s*(\d+(?:\.\d+)?)/);
            if (match) {
                const value = parseFloat(match[1]);
                return score >= value;
            }
        } else if (text.startsWith('>')) {
            const match = text.match(/>\s*(\d+(?:\.\d+)?)/);
            if (match) {
                const value = parseFloat(match[1]);
                return score > value;
            }
        } else if (text.startsWith('<=')) {
            const match = text.match(/<=\s*(\d+(?:\.\d+)?)/);
            if (match) {
                const value = parseFloat(match[1]);
                return score <= value;
            }
        } else if (text.startsWith('<')) {
            const match = text.match(/<\s*(\d+(?:\.\d+)?)/);
            if (match) {
                const value = parseFloat(match[1]);
                return score < value;
            }
        } else if (text.match(/^\d+(?:\.\d+)?\s*Punkte/)) {
            const match = text.match(/^(\d+(?:\.\d+)?)/);
            if (match) {
                const value = parseFloat(match[1]);
                return score === value;
            }
        }
        
        return false;
    });

    if (matchingVariant) {
        return { variantId: matchingVariant.id, variantText: matchingVariant.text };
    }

    return { variantId: null, variantText: null };
}

onVariantIdChanged() {
    let answerToSave: SingleItemAnswer;
    
    if (this.isRangeQuestion || this.isYesNoQuestion) {
        const selections = this.isRangeQuestion ? this.rangeSelections : this.yesNoSelections;
        answerToSave = new SingleItemAnswer(
            this.questionId, 
            [], 
            this.totalScore, 
            [JSON.stringify(selections)]
        );
        this.variantId = this.totalScore;
        this.variantText = JSON.stringify(selections);
    } else {
        answerToSave = new SingleItemAnswer(
            this.questionId, 
            [], 
            this.variantId, 
            [this.variantText]
        );
    }
    
    const dependentQuestions = this.localQuestions.filter(q => q.questionConditionId === this.property.id);
    const autoAnswerDependents = this.localQuestions.filter(q => 
        q.autoAnswer === true && q.referenceQuestionId === this.property.id
    );

    if(this.isRangeQuestion || this.isYesNoQuestion){
        if (dependentQuestions.length > 0 || autoAnswerDependents.length > 0) {
            const existingAnswer = this.answers.find(answer => answer.PropertyId === this.property.id);

            if (existingAnswer) {
                // Existing answer logic (show warning modal)
                let shouldShowWarning = false;
                
                if (this.isRangeQuestion || this.isYesNoQuestion) {
                    const oldScore = (existingAnswer as SingleItemAnswer).answer;
                    const newScore = this.totalScore;
                    shouldShowWarning = oldScore !== newScore;
                } else {
                    const oldAnswer = (existingAnswer as SingleItemAnswer).answer;
                    shouldShowWarning = oldAnswer !== this.variantId;
                }
                
                if (shouldShowWarning) {
                    this.dependentQuestionNames = this.getAllDependentQuestionNames(this.property.id);
                    
                    if (this.dependentQuestionNames.length > 0) {
                        this.warningModalVisible = true;
                        return; // Don't proceed, wait for modal
                    } else {
                        this.$events.emit("answerChanged", answerToSave);
                    }
                } else {
                    this.$events.emit("answerChanged", answerToSave);
                }
            } else {
                // First time answering - THIS IS WHERE THE FIX GOES
                
                // Process regular dependent questions first
                for (let j = 0; j < dependentQuestions.length; j++) {
                    const dependentQuestion = dependentQuestions[j];
                    
                    if (dependentQuestion && !dependentQuestion.autoAnswer) {
                        const valueToCheck = (this.isRangeQuestion || this.isYesNoQuestion) ? 
                            String(this.totalScore) : String(this.variantId);
                        
                        const isValueInList = dependentQuestion.answerQuestions?.includes(valueToCheck) || false;
                        let shouldShow = false;
                        
                        if (dependentQuestion.conditionQuestion === 'in') {
                            shouldShow = isValueInList;
                        } else if (dependentQuestion.conditionQuestion === 'notIn') {
                            shouldShow = !isValueInList;
                        }
                        
                        dependentQuestion.isHidden = !shouldShow;
                        
                        if (!shouldShow) {
                            this.checkDependentQuestion(dependentQuestion);
                        }
                    }
                }
                
                // Process auto-answer dependents AND their value-dependent questions
                autoAnswerDependents.forEach(autoQ => {
                    console.log('Processing auto-answer dependent:', autoQ.question);
                    
                    // Always hide auto-answer questions from UI
                    autoQ.isHidden = true;
                    
                    // Trigger re-evaluation
                    this.$events.emit('reevaluateAutoAnswer', autoQ.id);
                    
                    // NEW: Immediately resolve what the auto-answer will be
                    const currentScore = (this.isRangeQuestion || this.isYesNoQuestion) ? 
                        this.totalScore : this.variantId;
                    
                    if (currentScore !== null && currentScore !== undefined) {
                        const { variantId: resolvedVariantId, variantText: resolvedVariantText } = 
                            this.resolveAutoAnswerImmediate(autoQ, Number(currentScore));
                        
                        console.log(`Auto-answer "${autoQ.question}" resolved to variant ${resolvedVariantId}`);
                        
                        // NEW: Process questions that depend on this auto-answer question's VALUE
                        const valueDependentQuestions = this.localQuestions.filter(q => 
                            q.questionConditionId === autoQ.id
                        );
                        
                        valueDependentQuestions.forEach(valueDepQ => {
                            console.log(`Processing value-dependent question: ${valueDepQ.question}`);
                            
                            if (resolvedVariantId !== null) {
                                const variantStr = String(resolvedVariantId);
                                const isValueInList = valueDepQ.answerQuestions?.includes(variantStr) || false;
                                
                                let shouldShow = false;
                                if (valueDepQ.conditionQuestion === 'in') {
                                    shouldShow = isValueInList;
                                } else if (valueDepQ.conditionQuestion === 'notIn') {
                                    shouldShow = !isValueInList;
                                }
                                
                                // Auto-answer questions are always hidden
                                if (valueDepQ.autoAnswer === true) {
                                    valueDepQ.isHidden = true;
                                } else {
                                    valueDepQ.isHidden = !shouldShow;
                                    this.updateQuestionVisibilityInStore(valueDepQ.id, valueDepQ.isHidden);
                                }
                                
                                console.log(`Value-dependent question "${valueDepQ.question}" ${valueDepQ.isHidden ? 'HIDDEN' : 'SHOWN'}`);
                                
                                // If hiding, also hide its dependents
                                if (valueDepQ.isHidden) {
                                    this.checkDependentQuestion(valueDepQ);
                                }
                            } else {
                                // Could not resolve auto-answer, hide all its dependents
                                valueDepQ.isHidden = true;
                                this.checkDependentQuestion(valueDepQ);
                            }
                        });
                                             // Save the auto-answer with the resolved variant
                       if(resolvedVariantId !== null && resolvedVariantText !== null){
                            this.$events.emit("answerChanged", new SingleItemAnswer(
                                autoQ.id,
                                [],
                                resolvedVariantId,
                                [resolvedVariantText]
                            ));
                        }

                    }
                });
                
                
                this.$events.emit("answerChanged", answerToSave);
            }
        } else {
            this.$events.emit("answerChanged", answerToSave);
        }
    } else {
        // Non-range/yes-no questions logic remains the same
        if (dependentQuestions.length > 0) {
            const existingAnswer = this.answers.find(answer => answer.PropertyId === this.property.id);

            if (existingAnswer) {
                this.dependentQuestionNames = this.getAllDependentQuestionNames(this.property.id);
                this.warningModalVisible = true;
            } else {
                for (let j = 0; j < dependentQuestions.length; j++) {
                    const dependentQuestion = this.localQuestions.find(q => q.id == dependentQuestions[j].id);
                    
                    if (
                        (dependentQuestion?.answerQuestions?.includes(String(this.variantId)) && 
                        dependentQuestion.conditionQuestion === 'notIn') ||
                        (!dependentQuestion?.answerQuestions?.includes(String(this.variantId)) && 
                        dependentQuestion?.conditionQuestion === 'in')
                    ) {
                        dependentQuestion.isHidden = true;
                        this.checkDependentQuestion(dependentQuestion);
                    }
                }
                
                this.$events.emit("answerChanged", answerToSave);
            }
        } else {
            this.$events.emit("answerChanged", answerToSave);
        }
    }
}
    updateQuestionVisibilityInStore(questionId: number, isHidden: boolean) {
        console.log(`Syncing question ${questionId} hidden state: ${isHidden} to treatment store`);
        this.$store.commit('localQuestionsTreatment/setQuestionHidden', {
            id: questionId,
            isHidden
        });
    }

    @Watch('answers')
    onAnswersChanged() {
        // Re-check auto-answer when answers change
        if (this.property.autoAnswer && this.property.referenceQuestionId) {
            this.handleAutoAnswer();
        }
    }

    mounted() {
        this.$events.on('validate', () => {
            this.$v.$touch();
            if (this.$v.$anyError) {
                this.$events.emit('throwError', this.questionId);
            }
        });
    }

    beforeDestroy() {
        this.$events.off('validate');
        this.$events.off('reevaluateAutoAnswer');
    }

    showModal() {
        this.modalVisible = true;
    }
}
</script>

<style lang="scss" scoped>
/* ProxyMed Design System Variables */
$primary-color: #2f45a5;
$primary-gradient: linear-gradient(135deg, #2f45a5, #667bf2);
$secondary-color: #667bf2;
$danger-color: #f25260;
$success-color: #10b981;
$text-primary: #1e293b;
$text-secondary: #64748b;
$border-color: rgba(226, 232, 240, 0.5);
$bg-white: rgba(255, 255, 255, 0.95);
$shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
$shadow-hover: 0 8px 30px rgba(47, 69, 165, 0.12);
$radius: 12px;
$radius-sm: 8px;

/* Standard Radio Button Display */
.radio-option {
    background: $bg-white;
    backdrop-filter: blur(15px);
    border-radius: $radius-sm;
    padding: 1rem 1.25rem;
    margin-bottom: 0.75rem;
    border: 2px solid $border-color;
    box-shadow: $shadow;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: $shadow-hover;
        border-color: rgba(102, 123, 242, 0.3);
    }
}

.radio-input {
    width: 20px;
    height: 20px;
    margin: 0;
    margin-right: 1rem;
    cursor: pointer;
    accent-color: $primary-color;
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
}

.radio-label {
    flex: 1;
    font-size: 0.95rem;
    font-weight: 500;
    color: $text-primary;
    margin: 0;
    cursor: pointer;
    line-height: 1.5;
}

/* Range Question Styles */
.range-question-container {
    padding: 1.5rem 0;
}

.range-item {
    background: $bg-white;
    backdrop-filter: blur(15px);
    border-radius: $radius;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1rem;
    border: 1px solid $border-color;
    box-shadow: $shadow;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.3s ease;
    
    &:hover {
        box-shadow: $shadow-hover;
    }
    
    &:last-of-type {
        margin-bottom: 0;
    }
}

.range-item-text {
    flex: 1;
    padding-right: 1.5rem;
    font-size: 0.95rem;
    font-weight: 500;
    color: $text-primary;
    line-height: 1.6;
}

.range-options {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
}

.range-option {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border: 2px solid rgba(102, 123, 242, 0.3);
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    background: white;
    
    &:hover {
        border-color: $secondary-color;
        background: rgba(102, 123, 242, 0.05);
        transform: scale(1.1);
    }
    
    &.selected {
        border-color: $primary-color;
        background: $primary-gradient;
        color: white;
        box-shadow: 0 4px 15px rgba(47, 69, 165, 0.3);
        transform: scale(1.05);
    }
}

.range-radio {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.range-value {
    font-size: 0.9rem;
    font-weight: 600;
}

/* Yes/No Question Styles */
.yesno-question-container {
    padding: 1.5rem 0;
}

.yesno-item {
    background: $bg-white;
    backdrop-filter: blur(15px);
    border-radius: $radius;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1rem;
    border: 1px solid $border-color;
    box-shadow: $shadow;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.3s ease;
    
    &:hover {
        box-shadow: $shadow-hover;
    }
    
    &:last-of-type {
        margin-bottom: 0;
    }
}

.yesno-item-text {
    flex: 1;
    padding-right: 1.5rem;
    font-size: 0.95rem;
    font-weight: 500;
    color: $text-primary;
    line-height: 1.6;
}

.yesno-switch-container {
    position: relative;
}

/* Switch Styles - ProxyMed Design */
.switch {
    position: relative;
    display: inline-block;
    width: 90px;
    height: 40px;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #ef4444, #f87171);
    transition: .4s;
    border-radius: 40px;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.slider:before {
    position: absolute;
    content: "";
    height: 32px;
    width: 32px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

input:checked + .slider {
    background: linear-gradient(135deg, #10b981, #34d399);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

input:checked + .slider:before {
    transform: translateX(50px);
}

input:disabled + .slider {
    opacity: 0.5;
    cursor: not-allowed;
}

.switch-labels {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0 8px; /* Reduced padding to keep text away from edges */
    pointer-events: none;
    z-index: 1; /* Keep labels below the toggle circle */
}

.no-label, .yes-label {
    color: white;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0; /* Hidden by default */
    transition: opacity 0.3s ease;
}

/* Show NO label when switch is off (assuming a class like 'active' or checked state) */
.switch:not(.active) .no-label,
.switch:not(:checked) .no-label {
    opacity: 1;
}

/* Show YES label when switch is on */
.switch.active .yes-label,
.switch:checked .yes-label {
    opacity: 1;
}

/* Total Score Display - ProxyMed Style */
.total-score-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding: 1.25rem 1.5rem;
    border-radius: $radius;
    background: linear-gradient(135deg, rgba(102, 123, 242, 0.05), rgba(47, 69, 165, 0.02));
    border: 2px solid rgba(102, 123, 242, 0.2);
}

.total-score-label {
    font-size: 1rem;
    font-weight: 700;
    margin-right: 1rem;
    color: $text-primary;
}

.total-score-value {
    font-size: 1.75rem;
    font-weight: 900;
    background: $primary-gradient;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    min-width: 50px;
    text-align: center;
}

/* Action Buttons - ProxyMed Style */
.action-btns {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.875rem 1.75rem;
    border-radius: $radius-sm;
    font-weight: 600;
    font-size: 0.95rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    text-decoration: none;

    svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none !important;
    }
}

.btn-primary {
    background: $primary-gradient;
    color: white;
    box-shadow: 0 4px 15px rgba(47, 69, 165, 0.25);
    
    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(47, 69, 165, 0.35);
    }
}

.btn-saved {
    background: white;
    color: #2f45a5;
    font-weight: 600;
    height: 36px;
    padding: 0 1rem;
    border-radius: 8px;
    border: 2px solid rgba(47, 69, 165, 0.35);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
    cursor: pointer;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.btn-saved:not(:disabled):hover {
    background: linear-gradient(135deg, #2f45a5, #667bf2);
    border-color: transparent;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(47, 69, 165, 0.25);
}

.btn-saved:active:not(:disabled) {
    transform: translateY(0);
}

.btn-saved:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.btn-saved:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 123, 242, 0.2);
}


/* Responsive Styles */
@media only screen and (max-width: 599px) {
    .action-btns {
        flex-direction: column;
    }

    .radio-option {
        padding: 0.625rem 0.875rem;
        margin-bottom: 0.5rem;
    }

    .radio-label {
        font-size: 0.78rem;
        line-height: 1.5;
    }

    .radio-input {
        width: 18px;
        height: 18px;
        margin-right: 0.75rem;
        flex-shrink: 0;
    }

    .range-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .yesno-item {
        flex-direction: row;
        align-items: center;
        gap: 0.75rem;
        padding: 0.875rem 1rem;
    }

    .range-item-text {
        padding-right: 0;
        font-size: 0.78rem;
    }

    .yesno-item-text {
        padding-right: 0;
        font-size: 0.82rem;
    }

    .switch {
        width: 70px;
        height: 32px;
        flex-shrink: 0;
    }

    .slider {
        border-radius: 32px;
    }

    .slider:before {
        height: 24px;
        width: 24px;
        left: 4px;
        bottom: 4px;
    }

    input:checked + .slider:before {
        transform: translateX(38px);
    }

    .range-options {
        width: 100%;
        justify-content: flex-start;
        flex-wrap: wrap;
    }

    .total-score-container {
        justify-content: center;
    }
}

@media only screen and (min-width: 600px) {
    .action-btns {
        flex-direction: row;
        justify-content: space-between;
    }
}

@media only screen and (min-width: 768px) {
    .action-btns {
        flex-direction: column;
    }
}

@media only screen and (min-width: 992px) {
    .action-btns {
        flex-direction: row;
        justify-content: space-between;
    }

    .btn-saved {
        height: 44px;
        padding: 0 1.75rem;
        font-size: 1rem;
    }
}
</style>